import gql from 'graphql-tag';
import { clients } from '../../util/clients';
import { fieldsToGqlString } from '../../util/shared/vue-global';

const { backend } = clients.direct;

const defaultFields = {
  _id: '',
  fileQueueId: '',
  mortgageCompany: '',
  postingDate: '',
  timestamp: '',
  totalRecords: '',
  validRecords: '',
  overpaymentRecords: '',
  underpaymentRecords: '',
  notFoundRecords: '',
  duplicateRecords: '',
  totalOverpaymentAmount: '',
  totalUnderpaymentAmount: '',
  paymentApplied: '',
  records: {
    taxYear: '',
    taxId: '',
    propertyOrParcelId: '',
    mtgCode: '',
    nameFromMtgComp: '',
    loanNumber: '',
    paymentAmount: '',
    expectedAmount: '',
    calculatedPenalty: '',
    amountDifference: '',
    validationStatus: '',
    errorDescription: '',
    statusCode: '',
  },
};

export const MortgagePaymentImportActions = {
  async loadMortgagePaymentImport({ commit, dispatch }, { criteria, fields }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const gqlfields = fields || defaultFields;

      results = await backend.query({
        query: gql`query mortgagePaymentImports($criteria: mortgagePaymentImportCriteriaType) {
          mortgagePaymentImports(criteria: $criteria) {
            ${fieldsToGqlString(gqlfields)}
          }
        }`,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      const { mortgagePaymentImports } = results.data;
      return mortgagePaymentImports || [];
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading mortgage payment imports.', { root: true });
      return [];
    }
  },

  async createPaymentFromImport({ commit, dispatch }, {
    fileQueueId,
    workstation,
    applyOverPayments,
    skipPartials,
    skipDuplicates,
    skipPending,
    paymentType,
    checkNumber,
    eftType,
    creditType,
    paidBy,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation createPaymentFromMortgageImport(
          $fileQueueId: ID!
          $workstation: Int!
          $applyOverPayments: Boolean
          $skipPartials: Boolean
          $skipDuplicates: Boolean
          $skipPending: Boolean
          $paymentType: String
          $checkNumber: String
          $eftType: String
          $creditType: String
          $paidBy: String
        ) {
          createPaymentFromMortgageImport(
            fileQueueId: $fileQueueId
            workstation: $workstation
            applyOverPayments: $applyOverPayments
            skipPartials: $skipPartials
            skipDuplicates: $skipDuplicates
            skipPending: $skipPending
            paymentType: $paymentType
            checkNumber: $checkNumber
            eftType: $eftType
            creditType: $creditType
            paidBy: $paidBy
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          workstation,
          applyOverPayments,
          skipPartials,
          skipDuplicates,
          skipPending,
          paymentType,
          checkNumber,
          eftType,
          creditType,
          paidBy,
        },
      });

      clients.handleGQLErrors(results, commit);
      const { createPaymentFromMortgageImport } = results.data;
      return createPaymentFromMortgageImport;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem creating the payment.', { root: true });
      throw e;
    }
  },

  async updateTaxRecords({ commit, dispatch }, {
    fileQueueId, mtgCode, updateMode,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation updateTaxRecordsFromImport(
          $fileQueueId: ID!
          $mtgCode: String
          $updateMode: String
        ) {
          updateTaxRecordsFromImport(
            fileQueueId: $fileQueueId
            mtgCode: $mtgCode
            updateMode: $updateMode
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId, mtgCode, updateMode,
        },
      });

      clients.handleGQLErrors(results, commit);
      const { updateTaxRecordsFromImport } = results.data;
      return updateTaxRecordsFromImport;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating tax records.', { root: true });
      throw e;
    }
  },

  async getFileQueueStatus({ commit, dispatch }, { fileQueueId }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.query({
        query: gql`query getFileQueueStatus($fileQueueId: ID!) {
          fileQueue(_id: $fileQueueId) {
            _id
            Status
            Parameters
            ErrorDetail {
              LineNumber
              ErrorDescription
            }
          }
        }`,
        variables: { fileQueueId },
        fetchPolicy: 'network-only',
      });

      clients.handleGQLErrors(results, commit);
      const { fileQueue } = results.data;
      return fileQueue;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      throw e;
    }
  },

  async updateRecordValidationStatus({ commit, dispatch }, {
    fileQueueId, recordIndex, validationStatus,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation updateRecordValidationStatus(
          $fileQueueId: ID!
          $recordIndex: Int!
          $validationStatus: String!
        ) {
          updateRecordValidationStatus(
            fileQueueId: $fileQueueId
            recordIndex: $recordIndex
            validationStatus: $validationStatus
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          recordIndex,
          validationStatus,
        },
      });

      clients.handleGQLErrors(results, commit);
      if (results && results.data && results.data.updateRecordValidationStatus) {
        return results.data.updateRecordValidationStatus;
      }
      return { success: false, message: 'No response from server' };
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating the record status.', { root: true });
      throw e;
    }
  },
};

export default MortgagePaymentImportActions;
